// Shows the logged-in customer, in every price block, the price the cart would
// charge them (see Sonible\Pricing\Frontend for the markup and
// Sonible\Pricing\PersonalPrice for the answer). No arithmetic here: every price
// arrives as WooCommerce sale markup, and this only puts it in place.
//
// MARKUP CONTRACT
//   <span class="son-personal-price" data-son-product="{product id}" hidden></span>
//
// Every logged-in visitor gets the same cached copy of a page for hours, so
// neither the answer nor whose it is can be in the page. It is fetched from the
// REST route and kept in localStorage, valid for as long as the user cookie the
// answer sets, the woocommerce_cart_hash cookie and the page's currency are the
// ones it was built for. Sonible\Pricing\PersonalPrice drops it on the cart and the checkout,
// which change the cart without always updating that cookie, and on every guest
// page.
//
// Per node: a product the answer prices gets that price, any other keeps its
// public one. Every render is announced as a son-personal-price event, with the
// answer as its detail.

import { paintPrice } from './price-roll';

const MAX_AGE = 60 * 60 * 1000;
// how admin changes (discounts, settings, prices, the EDU flag, a completed
// order) reach a customer whose cart never changes
const REFRESH_AGE = 10 * 60 * 1000;
// a wp_rest nonce stays valid for 12 to 24 hours
const NONCE_MAX_AGE = 10 * 60 * 60 * 1000;

const config = () => window.sonPersonalPrice;

const storageKey = () => config().storageKey;
const nonceKey = () => `${config().storageKey}-nonce`;

let latestRequest = 0;

function cookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));

    return match ? decodeURIComponent(match[1]) : '';
}

// 0 until the first answer of this session sets it, which no stored entry matches
const userId = () => Number(cookie(config().userCookie));

// WooCommerce deletes the cookie for an empty cart, whose hash is ''.
const cartHash = () => cookie('woocommerce_cart_hash');

// An empty cart's hash is the same in every currency, so the hash alone would
// show prices in the currency the customer just switched away from.
const currency = () => config().currency;

function read(key) {
    try {
        return JSON.parse(window.localStorage.getItem(key));
    } catch (e) {
        return null;
    }
}

function write(key, value) {
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
        // storage blocked
    }
}

function clear(key) {
    try {
        window.localStorage.removeItem(key);
    } catch (e) {
        // storage blocked
    }
}

// Aged by this browser's clock rather than the server's: a skewed
// clock would otherwise make every entry stale, or none.
const age = (entry) => Date.now() - entry.builtAt;

function isValid(entry) {
    return (
        entry &&
        entry.userId === userId() &&
        entry.cartHash === cartHash() &&
        entry.currency === currency() &&
        Array.isArray(entry.owned) &&
        entry.prices &&
        entry.teasers &&
        age(entry) >= 0 &&
        age(entry) < MAX_AGE
    );
}

function render(entry) {
    document.querySelectorAll('.son-personal-price').forEach((node) => {
        paintPrice(node, entry.prices[node.dataset.sonProduct]);
    });

    document.dispatchEvent(new CustomEvent('son-personal-price', { detail: entry }));
}

// The cached page cannot carry a nonce: one is valid only for the session it
// was made in. So the nonce this browser last worked with is kept and tried
// first, and WordPress renews it on every answer in the X-WP-Nonce header.
function storedNonce() {
    const stored = read(nonceKey());
    const fresh = stored && stored.userId === userId() && Date.now() - stored.at < NONCE_MAX_AGE;

    return fresh ? stored.nonce : null;
}

function keepNonce(nonce) {
    if (nonce) {
        write(nonceKey(), { userId: userId(), nonce, at: Date.now() });
    }
}

async function fetchWithNonce(url, nonce) {
    const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-WP-Nonce': nonce },
    });
    if (response.ok) {
        keepNonce(response.headers.get('X-WP-Nonce'));
    }

    return { response, json: await response.json() };
}

// admin-ajax is never page-cached, so WordPress's own rest-nonce action hands
// out one for this session. Only without a working stored one: it boots all of
// wp-admin. Shared with completion-info-box.js.
export async function restFetch(url) {
    const stored = storedNonce();
    if (stored) {
        const answer = await fetchWithNonce(url, stored);
        if (answer.response.status !== 403 || answer.json.code !== 'rest_cookie_invalid_nonce') {
            return answer;
        }
    }

    const nonceResponse = await fetch(config().nonceEndpoint, { credentials: 'same-origin' });
    if (!nonceResponse.ok) {
        throw new Error('No REST nonce');
    }

    return fetchWithNonce(url, await nonceResponse.text());
}

// Only the newest request may paint, and only if the cart it priced is still
// the cart: a response can be overtaken by a cart change in this or another tab.
async function load(request) {
    let response;
    let json;
    try {
        ({ response, json } = await restFetch(config().endpoint));
    } catch (e) {
        return;
    }

    // a failure keeps whatever is shown
    if (
        request !== latestRequest ||
        !response.ok ||
        json.user_id !== userId() ||
        json.cart_hash !== cartHash() ||
        // priced in the cookie's currency, which a ?currency= page need not show
        json.currency !== currency()
    ) {
        return;
    }

    const entry = {
        userId: json.user_id,
        cartHash: json.cart_hash,
        currency: json.currency,
        builtAt: Date.now(),
        mode: json.mode,
        owned: json.owned,
        prices: json.prices,
        teasers: json.teasers,
    };
    // The Cart block changes the cart through the Store API, which fires no
    // event and does not reliably update the cookie: anything stored here could
    // outlive the cart it priced.
    if (!document.body.classList.contains('woocommerce-cart')) {
        write(storageKey(), entry);
    }
    render(entry);
}

// Announced whether or not the answer painted, so cart-lock.js is not left
// waiting on a request that failed.
async function refresh() {
    const request = ++latestRequest;

    try {
        await load(request);
    } finally {
        if (request === latestRequest) {
            document.dispatchEvent(new CustomEvent('son-personal-price-settled'));
        }
    }
}

function init() {
    if (!config()) {
        return;
    }
    if (!document.querySelector('.son-personal-price')) {
        return;
    }

    const entry = read(storageKey());
    if (!isValid(entry)) {
        refresh();
        return;
    }

    render(entry);
    if (age(entry) >= REFRESH_AGE) {
        refresh();
    }
}

document.addEventListener('DOMContentLoaded', () => {
    init();
    if (!config()) {
        return;
    }

    // added_to_cart also comes from the My Deals toggle
    if (window.jQuery) {
        // updated_wc_div: the cart page's own updates replace the form, and with it the suggestions' price nodes
        window.jQuery(document.body).on('added_to_cart removed_from_cart updated_wc_div', refresh);
    }
    document.body.addEventListener('wc-blocks_added_to_cart', refresh);
    document.body.addEventListener('wc-blocks_removed_from_cart', refresh);

    window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
            init();
        }
    });

    document.addEventListener('click', (event) => {
        if (event.target.closest('a[href*="customer-logout"], a[href*="action=logout"]')) {
            clear(storageKey());
            clear(nonceKey());
        }
    });
});
